/**
 * qualitySlice — Kalite Planı SSE event'lerinin canlı durumu.
 *
 * Kapsadığı akışlar (hepsi ADDITIVE — backend göndermezse slice boş
 * kalır ve hiçbir tüketici bileşen render olmaz):
 *   • `coverage.update`     → son kapsama matrisi (CoveragePanel + canlı bar)
 *   • `entity.graph`        → son varlık grafı snapshot'ı (EntityGraphPanel)
 *   • `investigation.plan`  → Kapsam-Güdümlü Soruşturma hücre defteri
 *     (InvestigationPanel + canlı bar) — yalnız `investigation` topolojisi
 *   • `turn.verify`      → turnId → doğrulama sonucu (VerifyBadge)
 *   • `turn.plan`        → turnId → konuşmacı mikro-planı (plan ipucu)
 *   • `moderator.summon` → bilirkişi çağrıları (band + witness rozeti)
 *   • `subagent.result`  → alt-ajan sonuç kartları (transcript akışı)
 *
 * Invariants: coverage/graph "last write wins" (backend zaten kümülatif
 * snapshot yollar); verify/plan turnId üzerinden idempotent overwrite;
 * summon + subagent listeleri SSE reconnect replay'ine karşı
 * fingerprint ile dedupe edilir (attachRagBatch ile aynı gerekçe).
 */
import type { StateCreator } from "zustand";
import type {
  CoverageMatrix,
  EntityGraph,
  InvestigationPlan,
  SubagentKind,
  SubagentResultPayload,
  TurnPlanPayload,
  TurnVerifyResult,
} from "../../domain/types";
import type { DebateStore } from "../debateStore";

export interface TurnVerifyEntry {
  verdict: TurnVerifyResult;
  healJumps: number;
}

export interface ModeratorSummonEntry {
  witnessId: string;
  witnessName: string;
  requirement: string;
  receivedAt: number;
}

export interface SubagentResultEntry {
  id: string;
  kind: SubagentKind;
  result: SubagentResultPayload;
  receivedAt: number;
}

/**
 * Monotonic sequence for subagent card ids — a wall-clock-only id
 * collides when two subagents land in the same millisecond (fan-out
 * verifier + compute is a real backend pattern).
 */
let subagentSeq = 0;

function subagentFingerprint(kind: SubagentKind, result: SubagentResultPayload): string {
  return [
    kind,
    result.kind,
    result.ok ? "1" : "0",
    result.summary,
    result.computation?.outputHash ?? "",
    [...result.evidenceUrls].sort().join(","),
  ].join("::");
}

export interface QualitySlice {
  /** Son `coverage.update` matrisi — canlı bar + verdict sekmesi fallback'i. */
  coverageMatrix: CoverageMatrix | null;
  /** Son `entity.graph` snapshot'ı. */
  entityGraph: EntityGraph | null;
  /** Son `investigation.plan` defteri — yalnız `investigation` topolojisi. */
  investigationPlan: InvestigationPlan | null;
  /** turnId → doğrulama sonucu (`turn.verify`). */
  verifyByTurnId: Record<string, TurnVerifyEntry>;
  /** turnId → konuşmacı planı (`turn.plan`). */
  planByTurnId: Record<string, TurnPlanPayload>;
  /** Kronolojik bilirkişi çağrıları (transcript bandı). */
  summons: ModeratorSummonEntry[];
  /** witnessId (speakerId) → çağrı — TranscriptTurn rozet lookup'ı O(1). */
  witnessById: Record<string, ModeratorSummonEntry>;
  /** Kronolojik alt-ajan sonuçları (transcript kartları). */
  subagentResults: SubagentResultEntry[];

  applyCoverageUpdate(matrix: CoverageMatrix): void;
  applyEntityGraph(graph: EntityGraph): void;
  applyInvestigationPlan(plan: InvestigationPlan): void;
  attachTurnVerify(turnId: string, entry: TurnVerifyEntry): void;
  attachTurnPlan(turnId: string, plan: TurnPlanPayload): void;
  addModeratorSummon(summon: Omit<ModeratorSummonEntry, "receivedAt">): void;
  addSubagentResult(kind: SubagentKind, result: SubagentResultPayload): void;
  resetQuality(): void;
}

export type QualitySliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  QualitySlice
>;

export const createQualitySlice: QualitySliceCreator = (set) => ({
  coverageMatrix: null,
  entityGraph: null,
  investigationPlan: null,
  verifyByTurnId: {},
  planByTurnId: {},
  summons: [],
  witnessById: {},
  subagentResults: [],

  applyCoverageUpdate: (matrix) => {
    set((state) => {
      state.coverageMatrix = matrix;
    });
  },

  applyEntityGraph: (graph) => {
    set((state) => {
      state.entityGraph = graph;
    });
  },

  applyInvestigationPlan: (plan) => {
    set((state) => {
      state.investigationPlan = plan;
    });
  },

  attachTurnVerify: (turnId, entry) => {
    if (!turnId) return;
    set((state) => {
      state.verifyByTurnId[turnId] = entry;
    });
  },

  attachTurnPlan: (turnId, plan) => {
    if (!turnId) return;
    set((state) => {
      state.planByTurnId[turnId] = plan;
    });
  },

  addModeratorSummon: (summon) => {
    if (!summon.witnessId) return;
    set((state) => {
      // Reconnect replay guard — the same summon frame may be delivered
      // twice; (witnessId, requirement) is the natural identity.
      const exists = state.summons.some(
        (s) =>
          s.witnessId === summon.witnessId &&
          s.requirement === summon.requirement,
      );
      if (exists) return;
      const entry: ModeratorSummonEntry = { ...summon, receivedAt: Date.now() };
      state.summons.push(entry);
      state.witnessById[summon.witnessId] = entry;
    });
  },

  addSubagentResult: (kind, result) => {
    set((state) => {
      const fp = subagentFingerprint(kind, result);
      const exists = state.subagentResults.some(
        (e) => subagentFingerprint(e.kind, e.result) === fp,
      );
      if (exists) return;
      subagentSeq += 1;
      state.subagentResults.push({
        id: `subagent-${Date.now()}-${subagentSeq}`,
        kind,
        result,
        receivedAt: Date.now(),
      });
    });
  },

  resetQuality: () => {
    set((state) => {
      state.coverageMatrix = null;
      state.entityGraph = null;
      state.investigationPlan = null;
      state.verifyByTurnId = {};
      state.planByTurnId = {};
      state.summons = [];
      state.witnessById = {};
      state.subagentResults = [];
    });
  },
});
